import React, { useState, useEffect } from "react";
import baseURL from "../../authComponents/baseURL/BaseURL";
import loginToken from "../../authComponents/localStorageItems/LoginTokin";
import groom from "../../../assets/icons/groom.svg";
import bride from "../../../assets/icons/bride.svg";
import { useNavigate } from "react-router-dom";

const getProperString = (string) => {
  if (!!string) {
    return string
      .split("_")
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  } else {
    return "--";
  }
};

const ProfileView = ({ setLoginPopUp }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [profileData, setProfileData] = useState(null);
  const [activeTab, setActiveTab] = useState("personal");

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        const response = await baseURL.get(`/profile`, {
          headers: {
            Authorization: `Bearer ${loginToken}`,
          },
        });
        if (response.status === 200) {
          setProfileData(response.data.data);
          setLoading(false);
        }
      } catch (error) {
        localStorage.removeItem("authToken");
        window.location.href = "/";
      }
    };

    if (loginToken) {
      fetchProfile();
    }
  }, [loginToken, setLoginPopUp, navigate]);

  const showEditForm = () => {
    navigate("/editform", { state: { profileData } });
  };

  const showChangePasswordForm = () => {
    navigate("/updatepassword");
  };

  function formatDisplayText(text) {
    if (!text) return "";
    return text
      .replace(/_/g, " ")
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  }

  if (loading) {
    return (
      <div className="min-h-screen flex justify-center items-center bg-gradient-to-br from-pink-50 to-red-50">
        <div className="relative w-24 h-24">
          <div className="absolute top-1/2 left-1/2 -ml-8 -mt-8 w-16 h-16 rounded-full bg-red-800 opacity-75 animate-ping"></div>
          <div
            className="absolute top-1/2 left-1/2 -ml-6 -mt-6 w-12 h-12 rounded-full bg-red-700 opacity-75 animate-ping"
            style={{ animationDelay: "0.3s" }}
          ></div>
          <div
            className="absolute top-1/2 left-1/2 -ml-4 -mt-4 w-8 h-8 rounded-full bg-red-500 opacity-75 animate-ping"
            style={{ animationDelay: "0.6s" }}
          ></div>
        </div>
      </div>
    );
  }

  const InfoItem = ({ label, value }) => (
    <div className="flex flex-col sm:flex-row py-3 border-b border-red-100">
      <span className="text-gray-700 font-medium w-full sm:w-1/2 mb-1 sm:mb-0">
        {label}:
      </span>
      <span className="text-gray-800 w-full sm:w-1/2 break-words">
        {value || "Not specified"}
      </span>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 to-red-50 py-4 sm:py-12 px-2 sm:px-4 font-sans">
      <div className="max-w-6xl mx-auto bg-white rounded-xl sm:rounded-2xl shadow-xl overflow-hidden">
        {/* Header Section */}
        <div className="relative">
          <div className="h-32 sm:h-40 bg-gradient-to-r from-red-500 to-red-700"></div>

          {/* Profile Image and Name - Mobile First Layout */}
          <div className="flex flex-col sm:flex-row items-center sm:items-end sm:absolute sm:top-20 sm:left-8">
            <div className="relative -mt-16 sm:mt-0 z-10">
              <div className="h-28 w-28 sm:h-40 sm:w-40 rounded-full border-4 border-white overflow-hidden bg-white shadow-lg mx-auto sm:mx-0">
                {profileData?.image_url === null ? (
                  profileData?.gender === "male" ? (
                    <img
                      src={groom}
                      className="w-full h-full object-cover"
                      alt="Male Default"
                    />
                  ) : (
                    <img
                      src={bride}
                      className="w-full h-full object-cover"
                      alt="Female Default"
                    />
                  )
                ) : (
                  <img
                    src={profileData?.image_url}
                    className="w-full h-full object-cover"
                    alt="User Profile"
                  />
                )}
              </div>
            </div>
            <div className="mt-3  sm:mt-0 sm:ml-6 sm:mb-20 text-center sm:text-left bg-white sm:bg-transparent rounded p-2 px-4">
              <h1 className="text-xl md:bg-white md:px-3 md:py-2 md:rounded md:mt-3 sm:text-xl font-sans font-semibold text-red-600">
                {profileData?.first_name} {profileData?.last_name}
              </h1>
            </div>
          </div>

          {/* Action Buttons - Responsive */}
          <div className="flex justify-center sm:justify-end mt-4 sm:mt-0 sm:absolute sm:top-4 sm:right-6 sm:space-x-3 px-4 sm:px-0 space-x-2">
            <button
              onClick={showEditForm}
              className="bg-white text-red-600 hover:bg-red-50 px-3 sm:px-5 py-2 text-sm sm:text-base rounded-lg shadow-md flex items-center transition duration-300"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
              <span className="whitespace-nowrap">Edit Profile</span>
            </button>
            <button
              onClick={showChangePasswordForm}
              className="bg-white text-red-600 hover:bg-red-50 px-3 sm:px-5 py-2 text-sm sm:text-base rounded-lg shadow-md flex items-center transition duration-300"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                />
              </svg>
              <span className="whitespace-nowrap">Change Password</span>
            </button>
          </div>
        </div>

        {/* Navigation Tabs - Scrollable on Mobile */}
        <div className="pt-6 sm:pt-24 px-2 sm:px-8">
          <div className="overflow-x-auto pb-2">
            <nav className="flex min-w-max border-b border-gray-200">
              <button
                onClick={() => setActiveTab("personal")}
                className={`pb-4 px-4 sm:px-6 font-medium text-base sm:text-lg transition-colors duration-300 ${
                  activeTab === "personal"
                    ? "border-b-2 border-red-500 text-red-600"
                    : "text-gray-500 hover:text-red-500"
                }`}
              >
                Personal Info
              </button>
              {/* <button
                onClick={() => setActiveTab("family")}
                className={`pb-4 px-4 sm:px-6 font-medium text-base sm:text-lg transition-colors duration-300 ${
                  activeTab === "family"
                    ? "border-b-2 border-red-500 text-red-600"
                    : "text-gray-500 hover:text-red-500"
                }`}
              >
                Family Details
              </button> */}
              <button
                onClick={() => setActiveTab("education")}
                className={`pb-4 px-4 sm:px-6 font-medium text-base sm:text-lg transition-colors duration-300 ${
                  activeTab === "education"
                    ? "border-b-2 border-red-500 text-red-600"
                    : "text-gray-500 hover:text-red-500"
                }`}
              >
                Education & Career
              </button>
              <button
                onClick={() => setActiveTab("contact")}
                className={`pb-4 px-4 sm:px-6 font-medium text-base sm:text-lg transition-colors duration-300 ${
                  activeTab === "contact"
                    ? "border-b-2 border-red-500 text-red-600"
                    : "text-gray-500 hover:text-red-500"
                }`}
              >
                Contact
              </button>
            </nav>
          </div>
        </div>

        {/* Content Section */}
        <div className="p-4 sm:p-8">
          {/* About Me / Bio Section */}
          {/* <div className="mb-6 sm:mb-8">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-3 sm:mb-4 font-sans">
              About Me
            </h2>
            <p className="text-gray-700 leading-relaxed bg-red-50 p-4 sm:p-6 rounded-lg">
              {profileData?.bio || "No bio information provided."}
            </p>
          </div> */}

          {/* Tab Content */}
          {activeTab === "personal" && (
            <div className="bg-white rounded-lg p-4 sm:p-6">
              <h2 className="text-xl sm:text-2xl font-sans font-bold text-red-600 mb-4 sm:mb-6 flex items-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 sm:h-6 sm:w-6 mr-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
                Personal Information
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                <div>
                  <InfoItem
                    label="Full Name"
                    value={`${profileData?.first_name} ${profileData?.last_name}`}
                  />
                  {/* <InfoItem label="Date of Birth" value={profileData?.dob} /> */}
                  <InfoItem label="Age" value={profileData?.age} />
                  <InfoItem label="Gender" value={getProperString(profileData?.gender)} />
                  <InfoItem
                    label="Marital Status"
                    value={formatDisplayText(profileData?.marital_status)}
                  />
                  <InfoItem
                    label="Religion"
                    value={formatDisplayText(profileData?.religion)}
                  />
                </div>
                <div>
                  <InfoItem label="Caste" value={profileData?.caste} />
                  <InfoItem label="Height" value={profileData?.height} />
                  <InfoItem label="Complexion" value={getProperString(profileData?.look)} />
                  <InfoItem label="Title" value={profileData?.title}/>
                  {/* <InfoItem
                    label="Blood Group"
                    value={profileData?.bloodGroup}
                  /> */}
                </div>
              </div>
            </div>
          )}

          {/* {activeTab === "family" && (
            <div className="bg-white rounded-lg p-4 sm:p-6">
              <h2 className="text-xl sm:text-2xl font-sans font-bold text-red-600 mb-4 sm:mb-6 flex items-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 sm:h-6 sm:w-6 mr-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
                Family Details
              </h2>
              <div className="bg-red-50 p-4 sm:p-6 rounded-lg mb-4 sm:mb-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-base sm:text-lg font-sans font-semibold text-red-700 mb-3">
                      Father's Information
                    </h3>
                    <InfoItem
                      label="Father's Name"
                      value={profileData?.fatherName}
                    />
                    <InfoItem
                      label="Father's Job"
                      value={profileData?.fatherJob}
                    />
                  </div>
                  <div className="mt-4 lg:mt-0">
                    <h3 className="text-base sm:text-lg font-sans font-semibold text-red-700 mb-3">
                      Mother's Information
                    </h3>
                    <InfoItem
                      label="Mother's Name"
                      value={profileData?.motherName}
                    />
                    <InfoItem
                      label="Mother's Job"
                      value={profileData?.motherJob}
                    />
                  </div>
                </div>
              </div>
              <div>
                <h3 className="text-base sm:text-lg font-sans font-semibold text-red-700 mb-3">
                  Siblings
                </h3>
                <p className="p-4 bg-gray-50 rounded-lg text-gray-700">
                  {profileData?.siblings || "No sibling information provided."}
                </p>
              </div>
            </div>
          )} */}

          {activeTab === "education" && (
            <div className="bg-white rounded-lg p-4 sm:p-6">
              <h2 className="text-xl sm:text-2xl font-sans font-bold text-red-600 mb-4 sm:mb-6 flex items-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 sm:h-6 sm:w-6 mr-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path d="M12 14l9-5-9-5-9 5 9 5z" />
                  <path d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998a12.078 12.078 0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222"
                  />
                </svg>
                Education & Career
              </h2>

              <div className="bg-red-50 p-4 sm:p-6 rounded-lg mb-4 sm:mb-6">
                <h3 className="text-base sm:text-lg font-sans font-semibold text-red-700 mb-3">
                  Educational Background
                </h3>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                  <InfoItem
                    label="Highest Degree"
                    value={formatDisplayText(profileData?.education)}
                  />
                  {/* <InfoItem label="College" value={profileData?.college} />
                  <InfoItem
                    label="Graduation Year"
                    value={profileData?.graduationYear}
                  /> */}
                </div>
              </div>

              <div className="bg-gray-50 p-4 sm:p-6 rounded-lg">
                <h3 className="text-base sm:text-lg font-sans font-semibold text-red-700 mb-3">
                  Career Information
                </h3>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                  <InfoItem
                    label="Occupation"
                    value={formatDisplayText(profileData?.occupation)}
                  />
                  {/* <InfoItem label="Company" value={profileData?.company} />
                  <InfoItem
                    label="Work Location"
                    value={profileData?.workLocation}
                  /> */}
                  <InfoItem label="Yearly Income" value={profileData?.income} />
                </div>
              </div>
            </div>
          )}

          {activeTab === "contact" && (
            <div className="bg-white rounded-lg p-4 sm:p-6">
              <h2 className="text-xl sm:text-2xl font-sans font-bold text-red-600 mb-4 sm:mb-6 flex items-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 sm:h-6 sm:w-6 mr-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
                Contact Information
              </h2>
              <div className="grid grid-cols-1 gap-4 sm:gap-6 bg-red-50 p-4 sm:p-6 rounded-lg">
                <div className="flex flex-col sm:flex-row items-center bg-white p-4 rounded-lg shadow">
                  <div className="bg-red-100 p-3 rounded-lg mb-3 sm:mb-0 sm:mr-4">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-6 w-6 text-red-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                      />
                    </svg>
                  </div>
                  <div className="text-center sm:text-left">
                    <p className="text-gray-500 text-sm">Phone Number</p>
                    <p className="font-medium text-gray-800">
                      {profileData?.mobile || "Not provided"}
                    </p>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row items-center bg-white p-4 rounded-lg shadow">
                  <div className="bg-red-100 p-3 rounded-lg mb-3 sm:mb-0 sm:mr-4">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-6 w-6 text-red-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                  <div className="text-center sm:text-left">
                    <p className="text-gray-500 text-sm">Email Address</p>
                    <p className="font-medium text-gray-800 break-all">
                      {profileData?.email || "Not provided"}
                    </p>
                  </div>
                </div>

                {/* <div className="flex flex-col sm:flex-row items-center bg-white p-4 rounded-lg shadow">
                  <div className="bg-red-100 p-3 rounded-lg mb-3 sm:mb-0 sm:mr-4">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-6 w-6 text-red-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                  </div>
                  <div className="text-center sm:text-left">
                    <p className="text-gray-500 text-sm">Address</p>
                    <p className="font-medium text-gray-800">
                      {profileData?.address || "Not provided"}
                    </p>
                  </div>
                </div> */}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfileView;
